/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/CompletionStatus" />
import * as React from "react";
import { GitConflictType, GitPullRequestCompletionOptions, GitPullRequestMergeStrategy } from "Repos/Common/Contracts/Git";
import { ICommitsTableProps } from "Repos/Common/Contracts/History";
import { getFileName, getIconNameForFile } from "Repos/Common/Util/File";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Ago } from "VSSUI/Ago";
import { Button, IButtonProps } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Icon, IconSize, IIconProps } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, List, ListItem } from "VSSUI/List";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Pill } from "VSSUI/Pill";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import {
    CheckEvaluation,
    IAbandonedData,
    IAutoCompletingData,
    ICompletedData,
    ICompletionStatus,
    IConflictsData,
    IMergeFailureData,
    IMergingData,
    IPullRequestPolicyEvaluation,
    IReviewer
} from "../../Contracts/PullRequest";
import { getPullRequestDataService } from "../../PullRequestDataService";
import { getPullRequestPolicyService } from "../../PullRequestPolicyService";
import { getPullRequestViewService, IPullRequestViewService } from "../../PullRequestViewService";
import { getChecksSummary, getReviewersSummary } from "../../Util/Check";
import { getReviewerRelatedPolicies } from "../../Util/Policy";

export function CompletionStatus() {
    const { pageContext } = useComponentContext();
    const prPolicyService = getPullRequestPolicyService(pageContext);
    const viewService = getPullRequestViewService(pageContext);

    const themeService = pageContext.getService<IVssThemeService>("IVssThemeService");
    themeService.createThemedSelector(".dark-build-output", t => (t.isDark ? t : themeService.getTheme("ms.vss-web.vsts-theme-dark")));

    return (
        <Card className="bolt-table-card">
            <div className="flex-column flex-grow scroll-hidden">
                {/* Overall status -- completing, auto-complete set, conflicts, etc */}
                <Observer completionStatus={viewService.getCompletionStatus()}>
                    {({ completionStatus }: { completionStatus: ICompletionStatus }) => {
                        return completionStatus.completedData ? (
                            <CompletedRow data={completionStatus.completedData} viewService={viewService} />
                        ) : completionStatus.abandonedData ? (
                            <AbandonedRow {...completionStatus.abandonedData} />
                        ) : completionStatus.completingData ? (
                            <CompletingRow {...completionStatus.completingData} />
                        ) : completionStatus.isSourceDeleted ? (
                            <SourceBranchDeletedRow />
                        ) : completionStatus.isTargetDeleted ? (
                            <TargetBranchDeletedRow />
                        ) : completionStatus.externallyMerged ? (
                            <ExternallyMergedRow />
                        ) : completionStatus.mergeFailureData ? (
                            <MergeFailureRow {...completionStatus.mergeFailureData} />
                        ) : completionStatus.autoCompletingData ? (
                            <AutoCompleteRow {...completionStatus.autoCompletingData} />
                        ) : null;
                    }}
                </Observer>

                {/* Checks status rollup */}
                <Observer
                    checks={prPolicyService.getCheckEvaluations()}
                    ready={prPolicyService.getPolicies().ready}
                    completionStatus={viewService.getCompletionStatus()}
                >
                    {({ checks, ready, completionStatus }: { checks: CheckEvaluation[]; ready: boolean; completionStatus: ICompletionStatus }) => {
                        if (!ready) {
                            return null;
                        }

                        const summary = getChecksSummary(checks, completionStatus);
                        if (summary.checksCount === 0) {
                            return null;
                        }

                        return (
                            <div className="separator-line-bottom padding-vertical-16 padding-horizontal-20 rhythm-vertical-16">
                                <StatusRow status={summary.didBypassPolicies ? Statuses.Skipped : summary.primaryStatus}>
                                    <div className="flex-row flex-center rhythm-horizontal-8">
                                        <span>{summary.primaryMessage}</span>
                                        {summary.didBypassPolicies && <Pill>{Resources.Bypassed}</Pill>}
                                    </div>
                                    <span className="flex-row flex-center secondary-text margin-vertical-4 rhythm-horizontal-4 body-s">
                                        {summary.secondaryStatus && <Status {...summary.secondaryStatus} size={StatusSize.s} />}
                                        <span>{summary.secondaryMessage}</span>
                                    </span>
                                </StatusRow>
                                <div className="status-details-container">
                                    {summary.previewChecks.length > 0 && (
                                        <List<CheckEvaluation>
                                            className="preview-check-list"
                                            itemProvider={new ArrayItemProvider(addClickFPSToChecks(summary.previewChecks, pageContext))}
                                            renderRow={renderPreviewCheck}
                                            singleClickActivation={false}
                                            width="100%"
                                        />
                                    )}
                                    {summary.hasMoreChecks && (
                                        <StatusDetailRow className="separator-line-top padding-left-4" key="view-all">
                                            <Link className="margin-16 margin-right-4" onClick={() => viewService.showChecksPanel()}>
                                                {format(Resources.ViewChecks, summary.checksCount)}
                                            </Link>
                                            <Icon
                                                ariaLabel={Resources.NumberOfDisplayedChecksIsLimited}
                                                iconName="Info"
                                                tooltipProps={{
                                                    renderContent: () => Resources.NumberOfDisplayedChecksIsLimited
                                                }}
                                            />
                                        </StatusDetailRow>
                                    )}
                                </div>
                            </div>
                        );
                    }}
                </Observer>

                {/* Reviewer status rollup */}
                <Observer
                    policies={prPolicyService.getPolicies()}
                    ready={prPolicyService.getPolicies().ready}
                    reviewers={viewService.getReviewers()}
                    isDraft={viewService.getIsDraft()}
                >
                    {({
                        policies,
                        ready,
                        reviewers,
                        isDraft
                    }: {
                        policies: IPullRequestPolicyEvaluation[];
                        ready: boolean;
                        reviewers: IReviewer[];
                        isDraft: boolean;
                    }) => {
                        if (!ready) {
                            return null;
                        }
                        const summary = getReviewersSummary(
                            pageContext,
                            getReviewerRelatedPolicies(policies),
                            reviewers,
                            viewService.getCreator().id,
                            viewService.getCommits().value[0]?.commit?.push?.pushedBy?.id,
                            isDraft
                        );

                        if (summary.isEmpty) {
                            return null;
                        }

                        return (
                            <StatusRow className="padding-vertical-16 padding-horizontal-20 separator-line-bottom" status={summary.status}>
                                <FormatComponent format={summary.message}>
                                    <span className="font-weight-semibold">{summary.soloDisplayName}</span>
                                    {summary.soloPendingVotes!}
                                </FormatComponent>
                            </StatusRow>
                        );
                    }}
                </Observer>

                {/* Merge status rollup */}
                <Observer completionStatus={viewService.getCompletionStatus()}>
                    {({ completionStatus }: { completionStatus: ICompletionStatus }) => {
                        return completionStatus.hasConflicts ? (
                            <ConflictsBanner value={viewService.getConflictsData()} />
                        ) : completionStatus.mergingData ? (
                            <MergingRow {...completionStatus.mergingData} />
                        ) : !completionStatus.abandonedData && completionStatus.lastMergeDate ? (
                            <NoConflictsRow lastMergeDate={completionStatus.lastMergeDate} />
                        ) : null;
                    }}
                </Observer>
            </div>
        </Card>
    );
}

export function addClickFPSToChecks(checks: CheckEvaluation[], pageContext: IVssPageContext): CheckEvaluation[] {
    return checks.map(check => {
        return {
            ...check,
            linkProps: check.linkProps &&
                check.linkProps.href && {
                    ...check.linkProps,
                    onClick: (event: React.SyntheticEvent<HTMLElement>) => {
                        if (!event.defaultPrevented) {
                            onClickFPS(pageContext, check.linkProps.href, true, event);
                        }
                    }
                }
        };
    });
}

export function CheckActionButton(props: { actions: IReadonlyObservableArray<IMenuItem> }) {
    return (
        <Observer actions={props.actions}>
            {({ actions }: { actions: IMenuItem[] }) => {
                if (actions.length === 0) {
                    return null;
                }

                if (actions.length === 1) {
                    const action = actions[0];

                    return (
                        <NoReentryButton
                            href={action.href}
                            onClick={event => {
                                if (action.onActivate) {
                                    action.onActivate(action, event);
                                    event.preventDefault();
                                }
                            }}
                            subtle
                            text={action.text}
                        />
                    );
                } else {
                    return <MoreButton contextualMenuProps={{ menuProps: { id: "check-actions", items: actions } }} />;
                }
            }}
        </Observer>
    );
}

function NoReentryButton(props: IButtonProps) {
    const [disabled, setDisabled] = React.useState(false);

    React.useEffect(() => {
        if (disabled) {
            const avoidReentryWhileMs = 14000;
            const timerId = setTimeout(() => setDisabled(false), avoidReentryWhileMs);
            return () => clearTimeout(timerId);
        }
    }, [disabled]);

    return (
        <Button
            {...props}
            disabled={disabled}
            onClick={event => {
                setDisabled(true);
                if (props.onClick) {
                    props.onClick(event);
                }
            }}
        />
    );
}

interface ICompletedRowProps {
    data: ICompletedData;
    viewService: IPullRequestViewService;
}

function CompletedRow(props: ICompletedRowProps) {
    const { size } = React.useContext(ScreenContext);
    const xsmall = size.value === ScreenSize.xsmall;

    const completeButtons = (
        <>
            <Button onClick={() => props.viewService.cherryPickPullRequest()}>{Resources.CherryPick}</Button>
            <Button onClick={() => props.viewService.revertPullRequest()} className="revert-button">
                {Resources.Revert}
            </Button>
        </>
    );

    return (
        <div className="flex-column separator-line-bottom padding-vertical-16 padding-horizontal-20 rhythm-vertical-16">
            <Crown icon={{ iconName: "BranchMerge", className: "circle-icon" }}>
                <div className="flex-row flex-grow flex-center">
                    <FormatComponent className="flex-grow" format={Resources.CompletedBy}>
                        {props.data.identity.displayName}
                        <Ago className="secondary-text margin-left-4" date={props.data.date} />
                    </FormatComponent>
                    {!xsmall && <div className="flex-row flex-noshrink justify-end">{completeButtons}</div>}
                </div>
            </Crown>
            {props.data.mergeCommit ? (
                <WrappedComponent<ICommitsTableProps>
                    wrappedType="repos-commits-table"
                    dependencies={["ms.vss-code-web.commits-list"]}
                    wrappedProps={{
                        className: "status-details-container",
                        commits: [props.data.mergeCommit]
                    }}
                />
            ) : (
                <MessageCard severity={MessageCardSeverity.Info}>{Resources.MergedExternally}</MessageCard>
            )}
            {xsmall && <div className="flex-row">{completeButtons}</div>}
            <CompletionDetails completionOptions={props.data.completionOptions} isFuture={false} />
        </div>
    );
}

function AutoCompleteRow(props: IAutoCompletingData) {
    return (
        <div className="flex-column separator-line-bottom padding-vertical-16 padding-horizontal-20 rhythm-vertical-16">
            <Crown icon={{ iconName: "TriggerAuto", className: "circle-icon auto-complete-icon font-weight-semibold" }}>
                <span className="font-weight-semibold">{Resources.AutoCompleteNotice} </span>
                <span>{format(Resources.SetToAutocompleteBy, props.autoCompleteSetBy.displayName)}</span>
            </Crown>
            <CompletionDetails completionOptions={props.completionOptions} isFuture={true} />
        </div>
    );
}

function CompletionDetails(props: { completionOptions: GitPullRequestCompletionOptions; isFuture: boolean }) {
    const { pageContext } = useComponentContext();
    const [areDetailsVisible, setDetailsVisible] = React.useState(false);

    let sourceDeletion: string | undefined;
    if (props.completionOptions.deleteSourceBranch) {
        if (props.isFuture) {
            sourceDeletion = Resources.SourceBranchWillBeDeleted;
        } else {
            const sourceRefStatus = getPullRequestDataService(pageContext).getPullRequestBasicData().value.sourceRefStatus;
            if (sourceRefStatus && sourceRefStatus.isDeleted) {
                sourceDeletion = Resources.SourceBranchWasDeleted;
            }
        }
    }

    return (
        <div>
            {areDetailsVisible && (
                <ul className="completion-details-list">
                    <li>{format(Resources.MergeStrategy, mergeStrategyTitles[props.completionOptions.mergeStrategy])}</li>
                    {sourceDeletion && <li>{sourceDeletion}</li>}
                    {props.completionOptions.transitionWorkItems && (
                        <li>{props.isFuture ? Resources.AssociatedWorkItemsWillBeCompleted : Resources.AssociatedWorkItemsWereCompleted}</li>
                    )}
                </ul>
            )}
            <Link onClick={() => setDetailsVisible(!areDetailsVisible)}>{areDetailsVisible ? Resources.HideDetails : Resources.ShowDetails}</Link>
        </div>
    );
}

const mergeStrategyTitles: { [key: number]: string } = {
    [GitPullRequestMergeStrategy.NoFastForward]: Resources.MergeTypeMerge,
    [GitPullRequestMergeStrategy.Squash]: Resources.MergeTypeSquashCommit,
    [GitPullRequestMergeStrategy.Rebase]: Resources.MergeTypeRebase,
    [GitPullRequestMergeStrategy.RebaseMerge]: Resources.MergeTypeSemiLinear
};

function AbandonedRow(props: IAbandonedData) {
    return (
        <Crown className="separator-line-bottom padding-vertical-16 padding-horizontal-20" icon={{ iconName: "Delete" }}>
            {Resources.WasAbandoned}
            &nbsp;
            <Ago className="secondary-text" date={props.date} />
        </Crown>
    );
}

const SpinnerStatus: IStatusProps = {
    color: "neutral",
    onRenderIcon: className => <Spinner className={css(className, "spinner-status")} size={SpinnerSize.medium} />
};

function CompletingRow(props: IMergingData) {
    return (
        <Crown className="separator-line-bottom padding-vertical-16 padding-horizontal-20" status={SpinnerStatus}>
            {props.isQueued ? Resources.CompletingMergeInProgress : Resources.QueuedForCompletion}
        </Crown>
    );
}

function SourceBranchDeletedRow() {
    return (
        <Crown className="separator-line-bottom padding-vertical-16 padding-horizontal-20" status={Statuses.Warning}>
            {Resources.SourceBranchWasDeleted}
        </Crown>
    );
}

function TargetBranchDeletedRow() {
    return (
        <Crown className="separator-line-bottom padding-vertical-16 padding-horizontal-20" status={Statuses.Warning}>
            {Resources.TargetBranchWasDeleted}
        </Crown>
    );
}

function ExternallyMergedRow() {
    return (
        <Crown className="separator-line-bottom padding-vertical-16 padding-horizontal-20" status={Statuses.Skipped}>
            {Resources.ExternallyMerged}
        </Crown>
    );
}

function MergeFailureRow(props: IMergeFailureData) {
    return (
        <MessageCard className="separator-line-bottom padding-vertical-16 padding-horizontal-20" severity={MessageCardSeverity.Warning}>
            {format(Resources.CompletionMergeFailed, props.message.startsWith("TF401022") ? Resources.UserFriendlyGitObjectTooLargeException : props.message)}
        </MessageCard>
    );
}

function renderPreviewCheck(index: number, check: CheckEvaluation, details: IListItemDetails<CheckEvaluation>): JSX.Element {
    const { buildEvaluation } = check;
    const buildOutputPreview = buildEvaluation && buildEvaluation.buildOutputPreview;

    return (
        <ListItem key={index} index={index} details={details} linkProps={check.linkProps} className="no-underline-link">
            <div className={css("flex-column flex-grow text-ellipsis", index > 0 && "separator-line-top")}>
                <StatusDetailRow className="flex-row flex-grow flex-center padding-horizontal-16" status={check.statusProps}>
                    <div className="pr-completion-status-header flex-grow rhythm-horizontal-8 text-ellipsis">
                        <span>{check.message}</span>
                        {
                            // Show status only for builds, so user knows when the build is Expired.
                            check.isBuild && <span className="secondary-text">{check.statusTitle}</span>
                        }
                    </div>
                    <CheckActionButton actions={check.getActions()} />
                </StatusDetailRow>
                {buildOutputPreview && buildOutputPreview.jobName && (
                    <div className="padding-horizontal-16 padding-vertical-8 text-ellipsis separator-line-top secondary-text body-s">
                        <span>
                            {buildOutputPreview.jobName} / {buildOutputPreview.taskName}
                        </span>
                    </div>
                )}
                {buildOutputPreview && buildOutputPreview.errors && buildOutputPreview.errors.length > 0 && (
                    <div className="flex-column">
                        {buildOutputPreview.errors.map(error => {
                            return (
                                <div className="flex-row monospaced-m dark-build-output" key={error.lineNumber}>
                                    <div className="line-number padding-horizontal-16">{error.lineNumber}</div>
                                    <div className="error-text word-break">{error.message}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </ListItem>
    );
}

function ConflictsBanner({ value }: { value: IReadonlyObservableValue<IConflictsData | undefined> }) {
    return (
        <Observer data={value}>
            {({ data }: { data: IConflictsData | undefined }) => {
                const message =
                    !data || data.tooMany
                        ? Resources.HasMergeConflicts
                        : data.hasSecondOrderConflicts
                        ? Resources.MergeConflictsSecondOrder
                        : data.conflicts.length === 1
                        ? Resources.OneMergeConflict
                        : format(Resources.MergeConflicts, data.conflicts.length);

                return (
                    <div className="padding-vertical-16 padding-horizontal-20 rhythm-vertical-16">
                        <StatusRow status={Statuses.Failed}>
                            <span>{message}</span>
                        </StatusRow>
                        {data && data.conflicts.length > 0 && (
                            <div className="status-details-container">
                                {data.conflicts.map((conflict, index) => (
                                    <StatusDetailRow
                                        className={css("padding-vertical-8 padding-horizontal-20 text-ellipsis", index > 0 && "separator-line-top")}
                                        key={conflict.conflictPath}
                                        icon={{ iconName: getIconNameForFile(conflict.conflictPath) }}
                                    >
                                        <Tooltip text={conflict.conflictPath}>
                                            <div className="flex-row flex-grow rhythm-horizontal-8 text-ellipsis">
                                                <div className="flex-grow">{getFileName(conflict.conflictPath)}</div>
                                                <div className="secondary-text text-ellipsis">
                                                    {getConflictTypeDescription(conflict.conflictType)}
                                                </div>
                                            </div>
                                        </Tooltip>
                                    </StatusDetailRow>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }}
        </Observer>
    );
}

function MergingRow(props: IMergingData) {
    return (
        <StatusRow className="padding-vertical-16 padding-horizontal-20" status={props.isQueued ? Statuses.Running : Statuses.Waiting}>
            <span>{props.isQueued ? Resources.MergeInProgress : Resources.QueuedForMerge}</span>
        </StatusRow>
    );
}

function NoConflictsRow({ lastMergeDate }: { lastMergeDate: Date }) {
    return (
        <StatusRow className="padding-vertical-16 padding-horizontal-20" status={Statuses.Success}>
            <span>{Resources.NoMergeConflicts}</span>
            <FormatComponent className="flex-grow secondary-text body-s" format={Resources.LastChecked}>
                <Ago date={lastMergeDate} />
            </FormatComponent>
        </StatusRow>
    );
}

interface CrownProps {
    className?: string;
    status?: IStatusProps;
    icon?: IIconProps;
}

/**
 * A row in the top that displays completion-related status. More padding and large icon.
 */
const Crown: React.FunctionComponent<CrownProps> = props => {
    return (
        <div className={css(props.className, "flex-column")}>
            <div className="flex-row flex-center rhythm-horizontal-8">
                {props.icon && <Icon {...props.icon} size={IconSize.small} />}
                {props.status && <Status {...props.status} className="margin-4" size={StatusSize.l} />}
                <div className="flex-grow">{props.children}</div>
            </div>
        </div>
    );
};

interface StatusRowProps {
    className?: string;
    status: IStatusProps;
    buttons?: IButtonProps[];
}

/**
 * A row that displays summarized info about checks or reviewers. Mid-size icon.
 */
const StatusRow: React.FunctionComponent<StatusRowProps> = props => {
    const { size } = React.useContext(ScreenContext);
    const xsmall = size.value === ScreenSize.xsmall;

    const statusAndText = (
        <>
            <Status {...props.status} className="margin-4" size={StatusSize.l} />
            <div className="flex-column flex-grow scroll-hidden">{props.children}</div>
        </>
    );
    const buttons = props.buttons && props.buttons.map((buttonProps, index) => <Button key={index} {...buttonProps} />);

    let row: JSX.Element;
    if (xsmall && props.buttons && props.buttons.length) {
        row = (
            <div className={css(props.className, "flex-column rhythm-vertical-16")}>
                <div className="flex-row flex-center rhythm-horizontal-8">{statusAndText}</div>
                <div className="flex-row flex-center rhythm-horizontal-8">{buttons}</div>
            </div>
        );
    } else {
        row = (
            <div className={css(props.className, "flex-row flex-center rhythm-horizontal-8")}>
                {statusAndText}
                {buttons}
            </div>
        );
    }
    return row;
};

interface StatusDetailRowProps {
    className?: string;
    status?: IStatusProps;
    icon?: IIconProps;
}

/**
 * A row that displays a detailed state of a check or conflict. Indented and large icon.
 */
const StatusDetailRow: React.FunctionComponent<StatusDetailRowProps> = props => {
    return (
        <div className={css(props.className, "flex flex-grow flex-center rhythm-horizontal-8")}>
            {props.icon && <Icon {...props.icon} size={IconSize.medium} />}
            {props.status && <Status {...props.status} size={StatusSize.m} />}
            {props.children}
        </div>
    );
};

const conflictTypeDescription: { [type: number]: string } = {
    [GitConflictType.None]: Resources.ConflictTypeNone,
    [GitConflictType.AddAdd]: Resources.ConflictTypeAddAdd,
    [GitConflictType.AddRename]: Resources.ConflictTypeAddRename,
    [GitConflictType.DeleteEdit]: Resources.ConflictTypeDeleteEdit,
    [GitConflictType.DeleteRename]: Resources.ConflictTypeDeleteRename,
    [GitConflictType.DirectoryChild]: Resources.ConflictTypeDirectoryChild,
    [GitConflictType.DirectoryFile]: Resources.ConflictTypeDirectoryFile,
    [GitConflictType.EditDelete]: Resources.ConflictTypeEditDelete,
    [GitConflictType.EditEdit]: Resources.ConflictTypeEditEdit,
    [GitConflictType.FileDirectory]: Resources.ConflictTypeFileDirectory,
    [GitConflictType.Rename1to2]: Resources.ConflictTypeRename1to2,
    [GitConflictType.Rename2to1]: Resources.ConflictTypeRename2to1,
    [GitConflictType.RenameAdd]: Resources.ConflictTypeRenameAdd,
    [GitConflictType.RenameDelete]: Resources.ConflictTypeRenameDelete,
    [GitConflictType.RenameRename]: Resources.ConflictTypeRenameRename
};

function getConflictTypeDescription(type: GitConflictType): string {
    return conflictTypeDescription[type] || Resources.ConflictTypeUnknown;
}
